import * as Sentry from '@sentry/vue';

/**
 * Utility functions for logging to Sentry with consistent formatting
 */

/**
 * Log form data to Sentry for debugging purposes
 * @param {Object} formData - The form data to log
 * @param {string} context - Additional context about where this is being called from
 * @param {Object} additionalData - Additional data to include in the log
 */
export const logFormDataToSentry = (formData, context = '', additionalData = {}) => {
  Sentry.addBreadcrumb({
    message: `Form data captured: ${context}`,
    category: 'form_data',
    level: 'info',
    data: {
      formData: JSON.parse(JSON.stringify(formData)), // Deep clone to avoid circular references
      context,
      url: window.location.href,
      pathname: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...additionalData,
    },
  });
};

/**
 * Log form validation errors to Sentry
 * @param {Object} validationErrors - The validation errors object
 * @param {string} formContext - Context about which form has errors
 */
export const logFormValidationErrors = (validationErrors, formContext = '') => {
  Sentry.captureMessage('Form validation errors', {
    level: 'warning',
    tags: {
      form_context: formContext,
      error_type: 'validation',
    },
    extra: {
      validationErrors,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Log form submission attempts to Sentry
 * @param {string} formType - Type of form being submitted
 * @param {Object} formData - The form data being submitted
 * @param {boolean} success - Whether the submission was successful
 */
export const logFormSubmission = (formType, formData, success = true) => {
  Sentry.addBreadcrumb({
    message: `Form submission ${success ? 'successful' : 'failed'}: ${formType}`,
    category: 'form_submission',
    level: success ? 'info' : 'error',
    data: {
      formType,
      success,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      formDataKeys: Object.keys(formData || {}), // Log keys but not sensitive data
    },
  });
};

/**
 * Log form step changes in multi-step forms
 * @param {number} fromStep - Previous step number
 * @param {number} toStep - New step number
 * @param {string} formType - Type of form
 */
export const logFormStepChange = (fromStep, toStep, formType = '') => {
  Sentry.addBreadcrumb({
    message: `Form step change: ${fromStep} -> ${toStep} (${formType})`,
    category: 'form_navigation',
    level: 'info',
    data: {
      fromStep,
      toStep,
      formType,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    },
  });
};

/**
 * Log document validation events
 * @param {string} documentType - Type of document being validated
 * @param {boolean} success - Whether validation was successful
 * @param {Object} additionalData - Additional data about the validation
 */
export const logDocumentValidation = (documentType, success = true, additionalData = {}) => {
  Sentry.addBreadcrumb({
    message: `Document validation ${success ? 'successful' : 'failed'}: ${documentType}`,
    category: 'document_validation',
    level: success ? 'info' : 'warning',
    data: {
      documentType,
      success,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...additionalData,
    },
  });
};

/**
 * Log photo capture events
 * @param {string} photoType - Type of photo captured (auto, manual, etc.)
 * @param {Object} photoData - Data about the captured photo
 * @param {Object} formData - Current form data context
 */
export const logPhotoCaptured = (photoType, photoData = {}, formData = {}) => {
  Sentry.addBreadcrumb({
    message: `Photo captured: ${photoType}`,
    category: 'photo_capture',
    level: 'info',
    data: {
      photoType,
      photoData: {
        hasPhoto: !!photoData,
        ...photoData,
      },
      formContext: {
        hasAutoPhoto: formData.auto_photo,
        requestType: formData.request_type?.name || 'unknown',
        employeeName: formData.name || 'unknown',
        employeeId: formData.identification || 'unknown',
      },
      url: window.location.href,
      pathname: window.location.pathname,
      timestamp: new Date().toISOString(),
    },
  });

  // Agregar tag para facilitar búsqueda
  Sentry.setTag('photo_loader_type', photoType);
};

/**
 * Log API requests with response time
 * @param {string} endpoint - API endpoint called
 * @param {string} method - HTTP method
 * @param {number} status - Response status code
 * @param {number} duration - Request duration in ms
 * @param {Object} additionalData - Additional data to log
 */
export const logApiRequest = (endpoint, method, status, duration, additionalData = {}) => {
  const level = status >= 400 ? 'error' : status >= 300 ? 'warning' : 'info';

  Sentry.addBreadcrumb({
    message: `API ${method} ${endpoint} - ${status}`,
    category: 'api_request',
    level,
    data: {
      endpoint,
      method,
      status,
      duration_ms: duration,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...additionalData,
    },
  });

  // Si la request falló, agregar tags adicionales
  if (status >= 400) {
    Sentry.setTag('api_error', 'true');
    Sentry.setTag('api_status', status.toString());
  }
};

/**
 * Log user actions for better debugging context
 * @param {string} action - User action performed
 * @param {string} component - Component where action occurred
 * @param {Object} data - Additional data about the action
 */
export const logUserAction = (action, component, data = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action} in ${component}`,
    category: 'user_action',
    level: 'info',
    data: {
      action,
      component,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...data,
    },
  });
};

/**
 * Log errors with detailed context
 * @param {Error} error - Error object
 * @param {string} context - Context where error occurred
 * @param {Object} additionalData - Additional debugging data
 */
export const logError = (error, context, additionalData = {}) => {
  Sentry.captureException(error, {
    tags: {
      error_context: context,
    },
    extra: {
      context,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...additionalData,
    },
  });
};

/**
 * Set user context for Sentry
 * @param {Object} user - User object
 */
export const setUserContext = (user) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id || user.user_id,
    email: user.email,
    username: user.username || user.name,
  });

  // Agregar contexto adicional sin información sensible
  Sentry.setContext('user_context', {
    role: user.role || 'unknown',
    company: user.company || 'unknown',
    department: user.department || 'unknown',
  });

  // Agregar tags para facilitar filtrado
  if (user.role) {
    Sentry.setTag('user_role', user.role);
  }
};

/**
 * Log performance measurements
 * @param {string} measurementName - Name of the measurement
 * @param {number} duration - Duration in milliseconds
 * @param {Object} data - Additional data
 */
export const logPerformance = (measurementName, duration, data = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${measurementName} took ${duration}ms`,
    category: 'performance',
    level: duration > 3000 ? 'warning' : 'info',
    data: {
      measurement: measurementName,
      duration_ms: duration,
      timestamp: new Date().toISOString(),
      ...data,
    },
  });

  // Si es lento, agregar un tag
  if (duration > 3000) {
    Sentry.setTag('slow_operation', measurementName);
  }
};
